/**
 * MapLibre `CustomLayerInterface` that renders the min-AGL raster with a slider
 * threshold + altitude-band color LUT in a fragment shader. One fetch per job;
 * slider + opacity + frequency changes are uniform swaps (no network).
 *
 * Reads a GeoTIFF via the `geotiff` library, uploads its single band as a
 * float32 texture, and draws a textured quad in Web-Mercator pixel coords.
 */

import type { Map as MLMap } from "maplibre-gl";
import { fromUrl, GeoTIFFImage } from "geotiff";

// Inlined shader sources — Vite would let us import these with ?raw, but we
// keep things simple here.
import vertSrc from "../shaders/viewshed.vert?raw";
import fragSrc from "../shaders/viewshed.frag?raw";

/** Convert WGS84 lat/lon to MapLibre's mercator coords (0..1 scaled internally). */
function lngLatToMercator(lng: number, lat: number): [number, number] {
  // MapLibre's projection is [-π, π] in x, with lat clamped. Use MercatorCoordinate if available.
  // We import MercatorCoordinate at runtime via the map instance to avoid a top-level dep.
  const MC = (window as any).maplibregl?.MercatorCoordinate;
  if (MC) {
    const mc = MC.fromLngLat({ lng, lat });
    return [mc.x, mc.y];
  }
  // Fallback Web Mercator
  const x = (lng + 180) / 360;
  const y = 0.5 - Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)) / (2 * Math.PI);
  return [x, y];
}

interface Bounds3857 {
  lngMin: number;
  latMin: number;
  lngMax: number;
  latMax: number;
}

/**
 * Duck-types MapLibre's `CustomLayerInterface`. We don't declare `implements` so
 * TS doesn't fight MapLibre's exact `render` signature (which changed across
 * versions — we accept either shape at runtime). Cast to the interface at
 * `map.addLayer()` call sites.
 */
export class MinAltitudeRasterLayer {
  readonly id: string;
  readonly type = "custom" as const;
  readonly renderingMode = "2d" as const;

  private program: WebGLProgram | null = null;
  private texture: WebGLTexture | null = null;
  private vbo: WebGLBuffer | null = null;
  private ibo: WebGLBuffer | null = null;
  private uMatrix: WebGLUniformLocation | null = null;
  private uRaster: WebGLUniformLocation | null = null;
  private uSlider: WebGLUniformLocation | null = null;
  private uOpacity: WebGLUniformLocation | null = null;
  private uNodata: WebGLUniformLocation | null = null;

  private rasterData: Float32Array | null = null;
  private rasterWidth = 0;
  private rasterHeight = 0;
  private bounds: Bounds3857 | null = null;
  /** Original EPSG:3857 bounds of the most recently loaded raster, kept so the
   * client-side composite can target the same grid (= bbox) instead of the
   * union of per-observer extents (which spill outside the user's drawn bbox). */
  private bounds3857: { left: number; bottom: number; right: number; top: number } | null = null;
  private nodata = -32767;

  /** Snapshot the raster + display state — used to rebuild a clean layer
   * after a basemap swap (MapLibre's custom-layer lifecycle across setStyle
   * is fragile, so recreating the layer is more reliable than recycling). */
  snapshot(): {
    data: Float32Array; width: number; height: number; nodata: number;
    bounds3857: { left: number; bottom: number; right: number; top: number };
    opacity: number; sliderAlt: number;
  } | null {
    if (!this.rasterData || !this.bounds3857 || this.rasterWidth === 0) return null;
    return {
      data: this.rasterData,
      width: this.rasterWidth,
      height: this.rasterHeight,
      nodata: this.nodata,
      bounds3857: this.bounds3857,
      opacity: this.opacity,
      sliderAlt: this.sliderAlt,
    };
  }

  /** EPSG:3857 bounds + dimensions of the currently bound raster. Used by the
   * client-side composite to sample into the same grid the union COG uses. */
  getRasterGrid(): { width: number; height: number; bounds: { left: number; bottom: number; right: number; top: number } } | null {
    if (!this.bounds3857 || this.rasterWidth === 0) return null;
    return { width: this.rasterWidth, height: this.rasterHeight, bounds: this.bounds3857 };
  }

  public sliderAlt = 5000;
  public opacity = 0.6;

  /** Total pixels in the raster (valid + nodata). */
  public totalPixels = 0;
  /** Pixels with valid (non-nodata) min-AGL values. */
  public validPixels = 0;
  /** Sorted list of valid altitude values; enables fast "how many ≤ X" via binary search. */
  private sortedValid: Float32Array | null = null;

  /** Fraction of total bbox pixels visible at or below `altFt`. */
  pctBboxAt(altFt: number): number {
    if (this.totalPixels === 0 || !this.sortedValid) return 0;
    // binary search for first value > altFt; everything before it is <= altFt
    const a = this.sortedValid;
    let lo = 0, hi = a.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (a[mid] <= altFt) lo = mid + 1;
      else hi = mid;
    }
    return lo / this.totalPixels;
  }

  /** Fraction of *valid* (ever-visible) pixels visible at or below `altFt`. */
  pctValidAt(altFt: number): number {
    if (this.validPixels === 0) return 0;
    return (this.pctBboxAt(altFt) * this.totalPixels) / this.validPixels;
  }

  constructor(id = "viewshed-min-alt") {
    this.id = id;
  }

  /** Fetch the COG from the given URL and populate the texture. */
  async loadCog(url: string): Promise<void> {
    const tiff = await fromUrl(url);
    const image: GeoTIFFImage = await tiff.getImage();
    this.rasterWidth = image.getWidth();
    this.rasterHeight = image.getHeight();
    this.nodata = Number(image.getGDALNoData() ?? -32767);

    const raster = await image.readRasters({ interleave: true });
    this.rasterData = new Float32Array(raster as Float32Array);

    const bbox = image.getBoundingBox(); // [minX, minY, maxX, maxY] in the raster's CRS
    this._setBoundsFromMercator(bbox[0], bbox[1], bbox[2], bbox[3]);
    this._computeStats();
  }

  /** Precompute total/valid/sorted valid values so pctBboxAt/pctValidAt are cheap. */
  private _computeStats(): void {
    const data = this.rasterData;
    if (!data) return;
    this.totalPixels = data.length;
    const nd = this.nodata;
    // First pass: count valid, collect in new array
    let validCount = 0;
    for (let i = 0; i < data.length; i++) {
      const v = data[i];
      if (v !== nd && Number.isFinite(v)) validCount++;
    }
    this.validPixels = validCount;
    const valid = new Float32Array(validCount);
    let j = 0;
    for (let i = 0; i < data.length; i++) {
      const v = data[i];
      if (v !== nd && Number.isFinite(v)) valid[j++] = v;
    }
    valid.sort();  // Float32Array.sort() sorts numerically, not lexicographically
    this.sortedValid = valid;
  }

  /** Replace texture data with a precomputed Float32 array (for client-side composite).
   * Caller supplies the same grid dimensions + EPSG:3857 bounds as the source COGs. */
  setRasterData(
    data: Float32Array,
    width: number,
    height: number,
    nodata: number,
    bounds3857: { left: number; bottom: number; right: number; top: number },
  ): void {
    this.rasterData = data;
    this.rasterWidth = width;
    this.rasterHeight = height;
    this.nodata = nodata;
    this._setBoundsFromMercator(bounds3857.left, bounds3857.bottom, bounds3857.right, bounds3857.top);
    this._computeStats();
    this.markDirty();
  }

  private _setBoundsFromMercator(left: number, bottom: number, right: number, top: number): void {
    const meterToLng = (x: number) => (x / 20037508.34) * 180;
    const meterToLat = (y: number) =>
      (Math.atan(Math.exp((y / 20037508.34) * Math.PI)) * 2 - Math.PI / 2) * (180 / Math.PI);
    this.bounds = {
      lngMin: meterToLng(left),
      latMin: meterToLat(bottom),
      lngMax: meterToLng(right),
      latMax: meterToLat(top),
    };
    this.bounds3857 = { left, bottom, right, top };
  }

  onAdd(_map: MLMap, glAny: WebGL2RenderingContext | WebGLRenderingContext): void {
    const gl = glAny as WebGL2RenderingContext;
    this.program = linkProgram(gl, vertSrc, fragSrc);
    this.uMatrix = gl.getUniformLocation(this.program, "u_matrix");
    this.uRaster = gl.getUniformLocation(this.program, "u_raster");
    this.uSlider = gl.getUniformLocation(this.program, "u_slider_alt");
    this.uOpacity = gl.getUniformLocation(this.program, "u_opacity");
    this.uNodata = gl.getUniformLocation(this.program, "u_nodata");

    this.vbo = gl.createBuffer();
    this.ibo = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 2, 0, 2, 3]), gl.STATIC_DRAW);

    this.texture = gl.createTexture();
  }

  onRemove(_map: MLMap, glAny: WebGL2RenderingContext | WebGLRenderingContext): void {
    const gl = glAny as WebGL2RenderingContext;
    if (this.program) gl.deleteProgram(this.program);
    if (this.vbo) gl.deleteBuffer(this.vbo);
    if (this.ibo) gl.deleteBuffer(this.ibo);
    if (this.texture) gl.deleteTexture(this.texture);
  }

  render(glAny: WebGL2RenderingContext | WebGLRenderingContext, argsOrMatrix: unknown): void {
    const gl = glAny as WebGL2RenderingContext;
    // MapLibre 4.x passes an options object with the matrix as a property; older
    // APIs passed the matrix directly. Handle both shapes.
    let matrix: number[] = [];
    if (Array.isArray(argsOrMatrix)) {
      matrix = argsOrMatrix as number[];
    } else if (argsOrMatrix && typeof argsOrMatrix === "object") {
      const obj = argsOrMatrix as Record<string, unknown>;
      matrix =
        (obj.defaultProjectionData && (obj.defaultProjectionData as any).mainMatrix) ||
        (obj.u_matrix as number[]) ||
        (obj.matrix as number[]) ||
        [];
    }
    if (!this.program || !this.rasterData || !this.bounds || !this.texture) return;

    // Always bind the texture before drawing — MapLibre unbinds between
    // passes, so binding only on upload makes subsequent slider/opacity
    // re-renders sample an unbound (default) texture and collapse to one band.
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);

    if ((this.texture as any).__dirty !== false) {
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      // R32F requires EXT_color_buffer_float; use LUMINANCE/FLOAT fallback for wider support
      const ext = gl.getExtension("OES_texture_float_linear");
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
      gl.texImage2D(
        gl.TEXTURE_2D, 0, gl.R32F,
        this.rasterWidth, this.rasterHeight, 0,
        gl.RED, gl.FLOAT, this.rasterData,
      );
      (this.texture as any).__dirty = false;
      void ext;
    }

    // Build quad in mercator coords for current bounds
    const [x0, y0] = lngLatToMercator(this.bounds.lngMin, this.bounds.latMax); // top-left
    const [x1, y1] = lngLatToMercator(this.bounds.lngMax, this.bounds.latMin); // bottom-right
    const verts = new Float32Array([
      x0, y0, 0, 0,   // top-left
      x1, y0, 1, 0,   // top-right
      x1, y1, 1, 1,   // bottom-right
      x0, y1, 0, 1,   // bottom-left
    ]);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, verts, gl.DYNAMIC_DRAW);

    gl.useProgram(this.program);
    const aPos = gl.getAttribLocation(this.program, "a_pos");
    const aUv = gl.getAttribLocation(this.program, "a_uv");
    gl.enableVertexAttribArray(aPos);
    gl.enableVertexAttribArray(aUv);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0);
    gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 16, 8);

    gl.uniformMatrix4fv(this.uMatrix!, false, matrix);
    gl.uniform1i(this.uRaster!, 0);
    gl.uniform1f(this.uSlider!, this.sliderAlt);
    gl.uniform1f(this.uOpacity!, this.opacity);
    gl.uniform1f(this.uNodata!, this.nodata);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
  }

  markDirty(): void {
    if (this.texture) (this.texture as any).__dirty = true;
  }
}

function linkProgram(gl: WebGL2RenderingContext, vertSrc: string, fragSrc: string): WebGLProgram {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vertSrc);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fragSrc);
  const program = gl.createProgram()!;
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error("Program link failed: " + gl.getProgramInfoLog(program));
  }
  return program;
}

function compileShader(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error("Shader compile failed: " + gl.getShaderInfoLog(shader));
  }
  return shader;
}
